import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { UsersByWorkShiftQueryVariables, useUsersByWorkShiftQuery } from "../../graphql"
import { UsersList } from "./UsersList"

export type UsersByWorkShiftProps = {
	workShiftId: string
}

export const UsersByWorkShift: React.FC<UsersByWorkShiftProps> = ({ workShiftId }) => {
	const [filter, setFilter] = useState({ workShiftId })

	const [pagination, setPagination] = useState<UsersByWorkShiftQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useUsersByWorkShiftQuery({
		variables: { ...filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ workShiftId })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [workShiftId])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.usersByWorkShift.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.usersByWorkShift ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading users</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.usersByWorkShift.users && data.usersByWorkShift.users.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.usersByWorkShift.users.length || 0} user
						{(data.usersByWorkShift.users.length || 0) > 1 ? "s" : ""}
					</Text>

					<UsersList users={data.usersByWorkShift.users} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more users</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any users.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
